/**
 * StandardCheckbox Component
 *
 * Unified checkbox with label and error support.
 * Consistent styling with dark mode support.
 */

import React from 'react';

export interface StandardCheckboxProps {
  id?: string;
  label?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  error?: string;
  helpText?: string;
  className?: string;
}

export const StandardCheckbox: React.FC<StandardCheckboxProps> = ({
  id,
  label,
  checked,
  onChange,
  disabled = false,
  error,
  helpText,
  className = ''
}) => {
  const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={className}>
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          id={checkboxId}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          className={`
            mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600
            text-blue-600 focus:ring-blue-500 focus:ring-2
            disabled:opacity-50 disabled:cursor-not-allowed
            ${error ? 'border-red-500 dark:border-red-500' : ''}
          `}
        />
        {label && (
          <label
            htmlFor={checkboxId}
            className={`
              text-sm font-medium select-none
              ${disabled ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' : 'text-gray-700 dark:text-gray-300 cursor-pointer'}
              ${error ? 'text-red-600 dark:text-red-400' : ''}
            `}
          >
            {label}
          </label>
        )}
      </div>

      {helpText && !error && (
        <p className="mt-1 ml-6 text-sm text-gray-500 dark:text-gray-400">
          {helpText}
        </p>
      )}

      {error && (
        <p className="mt-1 ml-6 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
};

export default StandardCheckbox;
